import type { RouteMount } from './types';
import { t } from '../../i18n';

/**
 * The Loadout route.
 *
 * `renderLoadoutPage` reaches Three.js (weapon GLBs in the Weapons tab, the operator figure in
 * the Operator one), so this file must never import `../../loadout-page.js`
 * statically; that would put the 2.8 MB Three.js chunk back on the shell's critical path for
 * every visitor, not just the ones who open this route. Same pattern as before: the slot is
 * claimed synchronously so it lands in the right place in the DOM regardless of when the chunk
 * arrives, and an `unmounted` flag is checked in the `.then` so navigating away mid-load never
 * renders into (or leaks a spin loop into) a detached element.
 *
 * No player-progress refresh here, unlike the armoury. The screen does read user-specific data
 * now (`GET /api/game/loadout` — what is unlocked, what is equipped), but it fetches that for
 * itself and nothing it writes moves the header's pills: equipping costs no currency and grants
 * no XP, so there is nothing for `playerProgressStore` to go stale about.
 */
export const mountLoadoutRoute: RouteMount = (outlet) => {
  const slot = document.createElement('div');
  outlet.append(slot);

  let disposeLoadout: (() => void) | null = null;
  let unmounted = false;

  void import('../../loadout-page.js')
    .then(({ renderLoadoutPage }) => {
      if (unmounted) return;
      disposeLoadout = renderLoadoutPage(slot);
    })
    .catch((error: unknown) => {
      if (unmounted) return;
      slot.textContent = t('loadout.unavailable', { message: (error as Error).message });
      slot.className = 'kit-well px-4 py-3 text-text-dim text-sm';
    });

  return () => {
    unmounted = true;
    disposeLoadout?.();
    disposeLoadout = null;
    slot.remove();
  };
};
